import type { SchemaFormValue } from "@southfields-digital/mxmz-react-ui";
import { useSyncExternalStore } from "react";

import { resolveDataConnectorUrl } from "../api/dataConnectorApiClient";

import { getPlaylistGraphicAutoUpdateListener } from "./playlistGraphicAutoUpdateBridge";

export type AutoUpdateConfig = {
	interval: number;
	pushWhenOnAir: boolean;
};

export type AutoUpdateStatus = "idle" | "connecting" | "connected" | "error";

export type AutoUpdateSnapshot = {
	isEnabled: boolean;
	status: AutoUpdateStatus;
	error?: string;
	config: AutoUpdateConfig;
	lastData?: unknown;
};

type AutoUpdateConnection = {
	playlistGraphicId: string;
	subscribePath: string;
	context: SchemaFormValue;
	config: AutoUpdateConfig;
	status: AutoUpdateStatus;
	error?: string;
	isEnabled: boolean;
	lastData?: unknown;
	teardown?: () => void;
	snapshot: AutoUpdateSnapshot;
};

type EnableAutoUpdateParams = {
	playlistGraphicId: string;
	subscribePath: string;
	context: SchemaFormValue;
	config: AutoUpdateConfig;
};

type AutoUpdateDataPayload = {
	playlistGraphicId: string;
	rawData: unknown;
	config: AutoUpdateConfig;
};

export type AutoUpdateDataListener = (payload: AutoUpdateDataPayload) => void | Promise<void>;

export const DEFAULT_AUTO_UPDATE_CONFIG: AutoUpdateConfig = {
	interval: 20,
	pushWhenOnAir: false,
};

const DEFAULT_SNAPSHOT: AutoUpdateSnapshot = {
	isEnabled: false,
	status: "idle",
	config: DEFAULT_AUTO_UPDATE_CONFIG,
};

class DataConnectorAutoUpdateManager {
	private connections = new Map<string, AutoUpdateConnection>();
	private listeners = new Set<() => void>();
	private dataListener?: AutoUpdateDataListener;

	enable(params: EnableAutoUpdateParams) {
		if (!params.subscribePath) {
			return;
		}

		const sanitizedConfig = this.sanitizeConfig(params.config);
		const existing = this.connections.get(params.playlistGraphicId);

		if (existing?.teardown) {
			existing.teardown();
		}

		const connection: AutoUpdateConnection = {
			playlistGraphicId: params.playlistGraphicId,
			subscribePath: params.subscribePath,
			context: { ...params.context },
			config: sanitizedConfig,
			status: "idle",
			error: undefined,
			isEnabled: true,
			lastData: existing?.lastData,
			snapshot: DEFAULT_SNAPSHOT,
		};

		this.connections.set(params.playlistGraphicId, connection);
		this.emit(connection);
		this.startStream(connection);
	}

	disable(playlistGraphicId: string) {
		const connection = this.connections.get(playlistGraphicId);
		if (!connection) {
			return;
		}

		connection.isEnabled = false;
		connection.status = "idle";
		connection.error = undefined;

		if (connection.teardown) {
			connection.teardown();
			connection.teardown = undefined;
		}

		this.emit(connection);
	}

	subscribe(listener: () => void) {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	}

	setDataListener(listener?: AutoUpdateDataListener) {
		this.dataListener = listener;
	}

	getSnapshot(playlistGraphicId?: string): AutoUpdateSnapshot {
		if (!playlistGraphicId) {
			return DEFAULT_SNAPSHOT;
		}

		const connection = this.connections.get(playlistGraphicId);
		if (!connection) {
			return DEFAULT_SNAPSHOT;
		}

		return connection.snapshot;
	}

	private emit(connection?: AutoUpdateConnection) {
		if (connection) {
			connection.snapshot = this.buildSnapshot(connection);
		}

		this.listeners.forEach((listener) => listener());
	}

	private sanitizeConfig(config: AutoUpdateConfig): AutoUpdateConfig {
		const normalizedInterval =
			Number.isFinite(config.interval) && config.interval > 0
				? Math.max(1, Math.floor(config.interval))
				: DEFAULT_AUTO_UPDATE_CONFIG.interval;

		return {
			interval: normalizedInterval,
			pushWhenOnAir: Boolean(config.pushWhenOnAir),
		};
	}

	private startStream(connection: AutoUpdateConnection) {
		if (typeof window === "undefined" || typeof window.EventSource === "undefined") {
			connection.isEnabled = false;
			connection.status = "error";
			connection.error = "EventSource is not supported in this environment.";
			this.emit(connection);
			return;
		}

		const url = this.buildSubscribeUrl(
			connection.subscribePath,
			connection.context,
			connection.config,
		);

		connection.status = "connecting";
		connection.error = undefined;
		this.emit(connection);

		const source = new window.EventSource(url, { withCredentials: true });

		const handleOpen = () => {
			connection.status = "connected";
			connection.error = undefined;
			this.emit(connection);
		};

		const handleData = (event: MessageEvent<string>) => {
			try {
				const payload = JSON.parse(event.data);
				connection.lastData = payload;
				this.emit(connection);
				void this.dataListener?.({
					playlistGraphicId: connection.playlistGraphicId,
					rawData: payload,
					config: connection.config,
				});
			} catch (err) {
				console.error("Failed to parse SSE data payload", err);
			}
		};

		const handleHeartbeat = () => undefined;

		const handleError = (event: MessageEvent<string> | Event) => {
			let message: string | undefined;

			if ("data" in event && event.data.length > 0) {
				try {
					const payload = JSON.parse(event.data);
					if (typeof payload?.error === "string") {
						message = payload.error;
					}
				} catch (err) {
					console.error("Failed to parse SSE error payload", err);
				}
			}

			connection.status = "error";
			connection.error = message ?? "Connection error";
			this.emit(connection);
		};

		const controller = new AbortController();
		const { signal } = controller;

		source.addEventListener("open", handleOpen, { signal });
		source.addEventListener("data", handleData, { signal });
		source.addEventListener("heartbeat", handleHeartbeat, { signal });
		source.addEventListener("error", handleError, { signal });

		connection.teardown = () => {
			controller.abort();
			source.close();
		};
	}

	private buildSubscribeUrl(
		subscribePath: string,
		context: SchemaFormValue,
		config: AutoUpdateConfig,
	): string {
		const resolvedEndpoint = resolveDataConnectorUrl(subscribePath);

		const params: Record<string, string> = {};

		Object.entries(context).forEach(([key, value]) => {
			if (value === undefined || value === null) {
				return;
			}

			if (typeof value === "object") {
				params[key] = JSON.stringify(value);
			} else {
				params[key] = String(value);
			}
		});

		params.interval = String(config.interval);

		const queryString = Object.entries(params)
			.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
			.join("&");

		return queryString ? `${resolvedEndpoint}?${queryString}` : resolvedEndpoint;
	}

	private buildSnapshot(connection: AutoUpdateConnection): AutoUpdateSnapshot {
		return {
			isEnabled: connection.isEnabled,
			status: connection.status,
			error: connection.error,
			config: connection.config,
			lastData: connection.lastData,
		};
	}
}

const autoUpdateManager = new DataConnectorAutoUpdateManager();
let playlistGraphicAutoUpdateBridgeRegistered = false;

const ensurePlaylistGraphicAutoUpdateBridge = () => {
	if (playlistGraphicAutoUpdateBridgeRegistered) {
		return;
	}

	autoUpdateManager.setDataListener(getPlaylistGraphicAutoUpdateListener());
	playlistGraphicAutoUpdateBridgeRegistered = true;
};

export const useAutoUpdateSnapshot = (playlistGraphicId?: string) =>
	useSyncExternalStore(
		(listener) => autoUpdateManager.subscribe(listener),
		() => autoUpdateManager.getSnapshot(playlistGraphicId),
		() => autoUpdateManager.getSnapshot(playlistGraphicId),
	);

export const isAutoUpdateEnabled = (playlistGraphicId: string) =>
	autoUpdateManager.getSnapshot(playlistGraphicId).isEnabled;

export const enableAutoUpdate = (params: EnableAutoUpdateParams) => {
	ensurePlaylistGraphicAutoUpdateBridge();
	autoUpdateManager.enable(params);
};

export const disableAutoUpdate = (playlistGraphicId: string) => {
	autoUpdateManager.disable(playlistGraphicId);
};
